import styled, { css } from 'styled-components'
import { stoColor, stoMedia, stoSpace } from 'constants/stoTheme'

/** Figma desktop frames: 64px padding from 992 up; the column gap is 32px, 64px from 1440. Below, it stacks. */
const DESKTOP = stoMedia.desktopUp
const XL_DESKTOP = stoMedia.xlDesktopUp

/**
 * The page's content width: 24 / 32 / 64px side padding, capped at the 1920 frame. Shared by
 * every band, so their columns line up. Left and right only: a `padding` shorthand here would
 * reset a band's own top and bottom padding.
 */
const contentWidth = css`
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  padding-left: ${stoSpace[6]};
  padding-right: ${stoSpace[6]};

  ${stoMedia.tabletUp} {
    padding-left: ${stoSpace[8]};
    padding-right: ${stoSpace[8]};
  }

  ${DESKTOP} {
    padding-left: ${stoSpace[16]};
    padding-right: ${stoSpace[16]};
  }
`

/**
 * Main column + aside, 2:1 as in Figma (832 / 416 at 1440, 1129 / 565 at 1920). The aside keeps
 * a 340px minimum near 992px, so the Invest box stays usable.
 */
const columns = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${stoSpace[8]};
  align-items: start;

  ${DESKTOP} {
    grid-template-columns: minmax(0, 2fr) minmax(340px, 1fr);
  }

  ${XL_DESKTOP} {
    gap: ${stoSpace[16]};
  }
`

/** Figma bands: full width, white, with a full-width rule below. Padding lives on `BandInner`. */
export const Band = styled.div`
  background: ${stoColor.surface};
  border-bottom: 1px solid ${stoColor.borderSubtle};
`

/**
 * 64px each side of a band's rule, and before the disclaimer, at every width. `$compactTop`: the
 * page's first band, 32px under the header on mobile.
 */
export const BandInner = styled.div<{ $compactTop?: boolean }>`
  ${contentWidth};
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[2]};
  padding-top: ${({ $compactTop }) => ($compactTop ? stoSpace[8] : stoSpace[16])};
  padding-bottom: ${stoSpace[16]};

  ${DESKTOP} {
    padding-top: ${stoSpace[16]};
  }
`

export const BandGrid = styled.div`
  ${columns};
`

/**
 * A band column: Figma spaces its cards by `$gap`, so the sections' own bottom margin is off.
 * Below desktop the sections are 32px apart, as the stacked columns are.
 */
export const BandColumn = styled.div<{ $gap: 4 | 8 | 10; $hideEmpty?: boolean }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => stoSpace[$gap === 10 ? 8 : $gap]};

  ${DESKTOP} {
    gap: ${({ $gap }) => stoSpace[$gap]};
  }

  & section[data-sto-section] {
    margin-bottom: 0;
  }

  /* A breakpoint wrapper whose section hid itself (How can I invest?, signed in, sale closed):
     no gap for nothing, so the column still starts level with the other. Opt-in: an empty
     element can be intentional, e.g. the KeyActions sheet's backdrop in the hero column. */
  ${({ $hideEmpty }) =>
    $hideEmpty &&
    css`
      & > :empty {
        display: none;
      }
    `}
`

/** Mobile: How can I invest? with the disclaimer 16px under it, closer than the column's gap. */
export const InvestGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stoSpace[4]};
`

/** The hero's KeyActions column. Below desktop KeyActions floats (sticky bar and sheet), so the
 *  column takes no room and adds no grid gap. */
export const HeroAside = styled(BandColumn)`
  ${stoMedia.belowDesktop} {
    display: contents;
  }
`
